/**
 * Открыть текущую страницу во встроенном браузере кошелька.
 *
 * Зачем. В обычном мобильном браузере подключение идёт через WalletConnect:
 * запрос на сессию публикуется на relay, кошелёк открывается по deep link,
 * поднимает своё соединение с тем же relay и только потом показывает окно
 * подтверждения. Это те самые несколько секунд пустого экрана MetaMask — и
 * та же цепочка, которая ломается целиком, если у проекта WalletConnect не
 * разрешён домен: кошелёк открывается, а подписывать ему нечего.
 * Если же открыть сайт внутри самого кошелька, он подставляет провайдер прямо
 * в страницу (window.ethereum / window.tronWeb / window.phantom.solana), и
 * подключение происходит мгновенно, без relay и без прыжков между
 * приложениями.
 *
 * Для TRON это вообще единственный путь: адаптера TRON у Reown нет. Для Solana
 * — фактически тоже: у Phantom нет поддержки WalletConnect, и AppKit на
 * телефоне всё равно уводит в его встроенный браузер, только молча.
 *
 * Ссылка несёт с собой одноразовый токен привязки (см. walletLink.ts): без
 * него страница внутри кошелька не может доказать серверу, чей это аккаунт,
 * и подключение остаётся внутри кошелька, не доходя до приложения.
 */

import { SupportedNetwork } from '../constants/networks';
import { WalletFamily } from './injectedWallets';
import {
  LINK_CHAIN_PARAM,
  LINK_FAMILY_PARAM,
  LINK_TOKEN_PARAM,
  requestLinkToken,
} from './walletLink';

export type { WalletFamily } from './injectedWallets';

/**
 * Как открывается кошелёк.
 *
 * `browser` — наша страница едет во встроенный браузер кошелька. Работает
 * везде, но человек остаётся в чужом WebView, где он не авторизован, и
 * возвращается руками.
 *
 * `deeplink` — родной протокол кошелька: подтвердил в кошельке и вернулся в
 * приложение. Круг замкнут, приложение остаётся тем же самым, вход не теряется.
 * Есть не у всех — пока только у Phantom (см. phantomDeepLink.ts).
 */
export type WalletOpenMode = 'browser' | 'deeplink';

export interface WalletBrowserOption {
  id: string;
  name: string;
  /** Для каких сетей кошелёк годится. */
  families: WalletFamily[];
  /** Чем полезен именно этот кошелёк — короткая подпись под названием. */
  hint: string;
  mode: WalletOpenMode;
  /** Только для mode === 'browser'. У родного протокола свой запуск. */
  buildUrl?: (pageUrl: string, family: WalletFamily) => string;
}

/** TronLink и TokenPocket принимают JSON в параметре param. */
function tronLinkOutside(pageUrl: string): string {
  const param = encodeURIComponent(
    JSON.stringify({
      url: pageUrl,
      action: 'open',
      protocol: 'TronLink',
      version: '1.0',
    })
  );
  return `tronlinkoutside://pull.activity?param=${param}`;
}

/** coin_id по SLIP-44: 60 — EVM, 195 — TRON, 501 — Solana. */
function trustCoinId(family: WalletFamily): number {
  if (family === 'tron') return 195;
  if (family === 'solana') return 501;
  return 60;
}

export const WALLET_BROWSER_OPTIONS: WalletBrowserOption[] = [
  {
    id: 'phantom-deeplink',
    name: 'Phantom',
    families: ['solana'],
    hint: 'Подтвердить в Phantom и вернуться сюда',
    mode: 'deeplink',
  },
  {
    id: 'phantom',
    name: 'Phantom · встроенный браузер',
    families: ['solana'],
    // Второй путь для Phantom оставлен не для симметрии: оплата с кошелька
    // (paySolana) идёт через провайдера в странице, а его даёт только
    // встроенный браузер. Родной протокол пока умеет подключение, но не подпись.
    hint: 'Нужен, чтобы платить прямо из кошелька',
    mode: 'browser',
    // Официальная universal link Phantom на встроенный браузер. ref нужен
    // самому Phantom для кнопки «назад к сайту» внутри его оболочки.
    buildUrl: (pageUrl) =>
      `https://phantom.app/ul/browse/${encodeURIComponent(pageUrl)}?ref=${encodeURIComponent(
        originOf(pageUrl)
      )}`,
  },
  {
    id: 'solflare',
    name: 'Solflare',
    families: ['solana'],
    hint: 'SOL, USDC',
    mode: 'browser',
    buildUrl: (pageUrl) =>
      `https://solflare.com/ul/v1/browse/${encodeURIComponent(pageUrl)}?ref=${encodeURIComponent(
        originOf(pageUrl)
      )}`,
  },
  {
    id: 'tronlink',
    name: 'TronLink',
    families: ['tron'],
    hint: 'TRX, USDT TRC-20',
    mode: 'browser',
    buildUrl: (pageUrl) => tronLinkOutside(pageUrl),
  },
  {
    id: 'trust',
    name: 'Trust Wallet',
    families: ['evm', 'tron', 'solana'],
    hint: 'EVM, TRON и Solana',
    mode: 'browser',
    buildUrl: (pageUrl, family) =>
      `https://link.trustwallet.com/open_url?coin_id=${trustCoinId(family)}&url=${encodeURIComponent(pageUrl)}`,
  },
  {
    id: 'metamask',
    name: 'MetaMask',
    families: ['evm'],
    hint: 'Polygon, BNB Chain, Ethereum',
    mode: 'browser',
    // metamask.app.link ждёт адрес без схемы.
    buildUrl: (pageUrl) => `https://metamask.app.link/dapp/${pageUrl.replace(/^https?:\/\//, '')}`,
  },
  {
    id: 'bitget',
    name: 'Bitget Wallet',
    families: ['evm', 'tron', 'solana'],
    hint: 'EVM, TRON и Solana',
    mode: 'browser',
    buildUrl: (pageUrl) => `https://bkapp.vip/dapp?url=${encodeURIComponent(pageUrl)}`,
  },
  {
    id: 'okx',
    name: 'OKX Wallet',
    families: ['evm', 'tron', 'solana'],
    hint: 'EVM, TRON и Solana',
    mode: 'browser',
    buildUrl: (pageUrl) => `okx://wallet/dapp/url?dappUrl=${encodeURIComponent(pageUrl)}`,
  },
];

function originOf(pageUrl: string): string {
  try {
    return new URL(pageUrl).origin;
  } catch {
    return 'https://webtransfer.app';
  }
}

export function walletBrowserOptions(family: WalletFamily): WalletBrowserOption[] {
  return WALLET_BROWSER_OPTIONS.filter((w) => w.families.includes(family));
}

/** Куда возвращать человека: текущий экран приложения. */
export function currentPageUrl(): string {
  if (typeof window === 'undefined') return 'https://webtransfer.app';
  return window.location.href;
}

/**
 * Дописать в адрес страницы приглашение на привязку. Токен уезжает в браузер
 * кошелька единственным каналом, который переживает переход между
 * приложениями, — адресной строкой.
 */
export function withLinkHandoff(
  pageUrl: string,
  token: string | null,
  chain: SupportedNetwork | null,
  family: WalletFamily
): string {
  try {
    const url = new URL(pageUrl);
    if (token) {
      url.searchParams.set(LINK_TOKEN_PARAM, token);
    }
    url.searchParams.set(LINK_FAMILY_PARAM, family);
    if (chain) {
      url.searchParams.set(LINK_CHAIN_PARAM, chain);
    }
    // Дубль в хвосте адреса. Посредники, через которых идут глубокие ссылки
    // (Branch у MetaMask, bkapp.vip у Bitget, link.trustwallet.com у Trust),
    // разбирают строку запроса как свою и до кошелька доносят её не всегда —
    // а без токена страница внутри кошелька просто не знает, чей это аккаунт,
    // и экран привязки не появляется вовсе. Хвост посредники не трогают.
    // Маршрутизация приложения на путях (BrowserRouter), так что хвост свободен.
    if (token && url.hash === '') {
      const copy = new URLSearchParams();
      copy.set(LINK_TOKEN_PARAM, token);
      copy.set(LINK_FAMILY_PARAM, family);
      if (chain) {
        copy.set(LINK_CHAIN_PARAM, chain);
      }
      url.hash = copy.toString();
    }
    return url.toString();
  } catch {
    return pageUrl;
  }
}

/**
 * Уйти в кошелёк. Токен запрашиваем до перехода: после того как браузер начал
 * навигацию в другое приложение, дописать что-либо в адрес уже нельзя.
 */
export async function openWalletBrowser(
  option: WalletBrowserOption,
  family: WalletFamily,
  chain: SupportedNetwork | null
): Promise<void> {
  if (typeof window === 'undefined' || !option.buildUrl) return;
  const token = await requestLinkToken(chain, family);
  const pageUrl = withLinkHandoff(currentPageUrl(), token, chain, family);
  window.location.href = option.buildUrl(pageUrl, family);
}
